import React from "react";
import {
  Modal,
  Box,
  Grid,
  Typography,
  Avatar,
  Button,
  Chip,
  Divider,
  Stack,
  alpha,
  useTheme,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { Close, Person, Email, Phone, CalendarToday, AccessTime, LocationOn, Message, School, AttachMoney } from "@mui/icons-material";
import { convertTo12Hour } from "../../utils/timeFormat";

const RequestDetailPage = ({ data, open, handleClose }) => {
  const theme = useMuiTheme();
  const { t } = useTranslation();
  
  if (!data) {
    return null;
  }

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      BackdropProps={{
        sx: {
          bgcolor: alpha('#000', 0.5),
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: 800 },
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
          maxHeight: '90vh',
          overflowY: 'auto',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {t('dashboard.requestDetails')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                  {t('dashboard.studentName')}
                </Typography>
                <Stack spacing={2}>
                  {data.guardianInfo && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: alpha(theme.palette.info.main, 0.08), 
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` 
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.info.main, 0.15)
                      }}>
                        <Person sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                          {data.guardianInfo.type === 'parent' ? 'Under Parent' : 'Under Business'}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: data.guardianInfo.email ? 0.5 : 0 }}>
                          {data.guardianInfo.name}
                        </Typography>
                        {data.guardianInfo.email && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {data.guardianInfo.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Person sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('common.name')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {`${data.studentId.userId.firstName} ${data.studentId.userId.lastName}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Email sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('common.email')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data.studentId.userId.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Phone sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('classRoom.phoneNumber')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data.studentId.userId.phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.secondary.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                  {t('dashboard.tutorName')}
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Person sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('common.name')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {`${data.tutorId.userId.firstName} ${data.tutorId.userId.lastName}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Email sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('common.email')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data.tutorId.userId.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Phone sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('classRoom.phoneNumber')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data.tutorId.userId.phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                  {t('dashboard.tutoringDetails')}
                </Typography>
                <Stack spacing={2}>
                  {data.startingTime && !data.startTime && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.startingTime')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.startingTime}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {data.date && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.preferredDate')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(data.date).toLocaleDateString(undefined, {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {data.startTime && data.endTime && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.timeWindow') || 'Time Window'}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {convertTo12Hour(data.startTime)} - {convertTo12Hour(data.endTime)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {data.duration && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.duration')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.duration === 'ONE_TIME' ? t('home.oneTime') : t('home.recurring')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('dashboard.tutoringType')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        {data.tutoringType.map((type) => (
                          <Chip
                            key={type}
                            label={type === "IN_PERSON" ? t('common.inPerson') : t('common.online')}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AttachMoney sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('dashboard.tutorRate')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data.tutorId.rate} ETB/hour
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                  {t('dashboard.additionalInformation')}
                </Typography>
                <Stack spacing={2}>
                  {data.message && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Message sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                          {t('dashboard.message')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 400 }}>
                          {data.message}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${t('dashboard.adminStatus')}: ${data.adminApproved || 'N/A'}`}
                      size="small"
                      color={data.adminApproved === 'APPROVED' ? 'success' : data.adminApproved === 'REJECTED' ? 'error' : 'warning'}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={`${t('dashboard.tutorStatus')}: ${data.status || 'N/A'}`}
                      size="small"
                      color={data.status === 'Approved' ? 'success' : data.status === 'Rejected' ? 'error' : 'warning'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Stack>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ 
                borderRadius: 2,
                py: 1.25,
                px: 3,
                fontWeight: 600,
              }}
            >
              {t('dashboard.close')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const Card = ({ children, ...props }) => (
  <Box {...props}>{children}</Box>
);

export default RequestDetailPage;
